import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "http://localhost:5000";

function getVisitorId() {
  let visitorId =
    localStorage.getItem("ds_visitor_id");

  if (!visitorId) {
    visitorId = crypto.randomUUID();

    localStorage.setItem(
      "ds_visitor_id",
      visitorId,
    );
  }

  return visitorId;
}

function getSessionId() {
  let sessionId =
    sessionStorage.getItem("ds_session_id");

  if (!sessionId) {
    sessionId = crypto.randomUUID();

    sessionStorage.setItem(
      "ds_session_id",
      sessionId,
    );
  }

  return sessionId;
}

async function sendAnalytics(url, payload) {
  try {
    await fetch(`${API_URL}${url}`, {
      method: "POST",

      credentials: "include",

      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify(payload),

      keepalive: true,
    });
  } catch (error) {
    console.error(
      "Analytics request failed:",
      error,
    );
  }
}

export default function AnalyticsTracker() {
  const location = useLocation();

  const currentPageViewRef = useRef(null);

  const sessionStartRef = useRef(
    Date.now(),
  );

  const visitorIdRef = useRef(
    getVisitorId(),
  );

  const sessionIdRef = useRef(
    getSessionId(),
  );

  /*
   * Start session once
   */
  useEffect(() => {
    const visitorId =
      visitorIdRef.current;

    const sessionId =
      sessionIdRef.current;

    sendAnalytics(
      "/api/v1/analytics/session/start",
      {
        visitorId,
        sessionId,

        landingPage:
          window.location.pathname +
          window.location.search,

        referrer: document.referrer,

        device: {
          userAgent:
            navigator.userAgent,

          language:
            navigator.language,

          platform:
            navigator.platform,

          screenWidth:
            window.screen.width,

          screenHeight:
            window.screen.height,
        },
      },
    );
  }, []);

  /*
   * Track React Router page changes
   */
  useEffect(() => {
    const visitorId =
      visitorIdRef.current;

    const sessionId =
      sessionIdRef.current;

    /*
     * Close previous page
     */
    if (currentPageViewRef.current) {
      const previous =
        currentPageViewRef.current;

      const durationMs =
        Date.now() -
        previous.enteredAt;

      sendAnalytics(
        "/api/v1/analytics/page-view/end",
        {
          pageViewId:
            previous.pageViewId,

          exitedAt:
            new Date().toISOString(),

          durationMs,
        },
      );
    }

    /*
     * Start new page view
     */
    const pageViewId =
      crypto.randomUUID();

    const enteredAt =
      Date.now();

    const path =
      location.pathname +
      location.search;

    currentPageViewRef.current = {
      pageViewId,
      enteredAt,
      path,
    };

    sendAnalytics(
      "/api/v1/analytics/page-view/start",
      {
        pageViewId,
        sessionId,
        visitorId,
        path,

        title: document.title,

        enteredAt:
          new Date(
            enteredAt,
          ).toISOString(),
      },
    );
  }, [
    location.pathname,
    location.search,
  ]);

  /*
   * User closes tab / browser
   */
  useEffect(() => {
    const handlePageHide = () => {
      const currentPage =
        currentPageViewRef.current;

      if (currentPage) {
        const durationMs =
          Date.now() -
          currentPage.enteredAt;

        fetch(
          `${API_URL}/api/v1/analytics/page-view/end`,
          {
            method: "POST",

            credentials: "include",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              pageViewId:
                currentPage.pageViewId,

              exitedAt:
                new Date().toISOString(),

              durationMs,
            }),

            keepalive: true,
          },
        ).catch(() => {});
      }

      const sessionDuration =
        Date.now() -
        sessionStartRef.current;

      fetch(
        `${API_URL}/api/v1/analytics/session/end`,
        {
          method: "POST",

          credentials: "include",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({
            sessionId:
              sessionIdRef.current,

            durationMs:
              sessionDuration,
          }),

          keepalive: true,
        },
      ).catch(() => {});
    };

    window.addEventListener(
      "pagehide",
      handlePageHide,
    );

    return () => {
      window.removeEventListener(
        "pagehide",
        handlePageHide,
      );
    };
  }, []);

  return null;
}